import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, SkipForward, Send, RefreshCcw, Loader2, Check } from 'lucide-react';

/**
 * Reusable interactive Q&A refiner for any Iris AI feature.
 *
 * Props:
 *   questions   — string[] of clarifying questions returned by AI
 *   onRefine    — async (qaPairs: [{q,a}]) => void  (called when user hits "Refine")
 *   refining    — boolean  (parent controls loading state)
 *   refinedCount — number  (how many refinements have happened; used for label)
 *   compact     — boolean  (smaller layout for cards)
 */
export default function AIRefiner({ questions = [], onRefine, refining = false, refinedCount = 0, compact = false }) {
  const [answers, setAnswers] = useState({});   // idx -> string
  const [skipped, setSkipped] = useState({});   // idx -> true
  const [current, setCurrent] = useState(0);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  // reset when new questions arrive
  useEffect(() => {
    setAnswers({});
    setSkipped({});
    setCurrent(0);
    setDraft('');
  }, [questions.join('||')]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, [current]);

  if (!questions?.length) return null;

  const answeredCount = Object.keys(answers).length;
  const doneCount = answeredCount + Object.keys(skipped).length;
  const allDone = doneCount >= questions.length;

  const submitCurrent = () => {
    const val = draft.trim();
    if (!val) return skipCurrent();
    setAnswers({ ...answers, [current]: val });
    setDraft('');
    setCurrent(c => Math.min(c + 1, questions.length));
  };

  const skipCurrent = () => {
    setSkipped({ ...skipped, [current]: true });
    setDraft('');
    setCurrent(c => Math.min(c + 1, questions.length));
  };

  const runRefine = () => {
    const pairs = questions.map((q, i) => ({ q, a: answers[i] || '' }));
    onRefine(pairs);
  };

  return (
    <div className={`rounded-2xl border border-amber-200/70 bg-amber-50/60 ${compact ? 'p-4' : 'p-6'}`} data-testid="ai-refiner">
      <div className="flex items-center justify-between mb-3">
        <div className="overline text-amber-900 flex items-center gap-2">
          <Sparkles size={12}/> Iris AI wants to know
          <span className="normal-case tracking-normal text-[10px] text-amber-800/70 font-medium">
            {doneCount}/{questions.length} answered
          </span>
        </div>
        {refinedCount > 0 && (
          <span className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700 bg-emerald-100/70 px-2 py-1 rounded-full">
            Refined ×{refinedCount}
          </span>
        )}
      </div>

      {/* Answered history */}
      <div className="space-y-2 mb-3">
        {questions.map((q, i) => {
          if (i >= current) return null;
          const ans = answers[i];
          const wasSkipped = skipped[i];
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-sm space-y-1"
              data-testid={`ai-refiner-answered-${i}`}
            >
              <div className="text-amber-900/80 flex items-start gap-2">
                <Check size={14} className="text-emerald-700 mt-0.5 flex-shrink-0"/>
                <span className="flex-1">{q}</span>
              </div>
              <div className={`ml-6 text-sm ${wasSkipped ? 'text-slate-400 italic' : 'text-slate-800'}`}>
                {wasSkipped ? '(skipped)' : ans}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Current question */}
      <AnimatePresence mode="wait">
        {!allDone && (
          <motion.div
            key={current}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="space-y-2"
          >
            <div className="text-sm text-amber-950 font-medium" data-testid="ai-refiner-current-q">
              {questions[current]}
            </div>
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); submitCurrent(); }
                }}
                placeholder="Type your answer…"
                data-testid="ai-refiner-input"
                className="flex-1 px-3 py-2 rounded-xl border border-amber-200 bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
              />
              <button
                type="button"
                onClick={submitCurrent}
                disabled={!draft.trim()}
                data-testid="ai-refiner-answer-btn"
                className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-40"
              >
                <Send size={13}/> Answer
              </button>
              <button
                type="button"
                onClick={skipCurrent}
                data-testid="ai-refiner-skip-btn"
                className="btn-ghost text-sm flex items-center gap-1.5 text-slate-600"
              >
                <SkipForward size={13}/> Skip
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Refine CTA */}
      {allDone && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pt-2">
          <div className="text-xs text-amber-900/80 mb-2">
            {answeredCount > 0
              ? `Iris AI will now refine the analysis using your ${answeredCount} answer${answeredCount === 1 ? '' : 's'}.`
              : 'No answers provided — you can still request a refinement or answer some questions first.'}
          </div>
          <button
            type="button"
            onClick={runRefine}
            disabled={refining || answeredCount === 0}
            data-testid="ai-refiner-refine-btn"
            className="btn-gold text-sm flex items-center gap-2 disabled:opacity-50"
          >
            {refining
              ? <><Loader2 size={13} className="animate-spin"/> Iris AI is refining…</>
              : <><RefreshCcw size={13}/> Refine analysis with my answers</>}
          </button>
        </motion.div>
      )}
    </div>
  );
}
